import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canAccessDept, isSuperAdmin, ROLE_LABEL, DEPT_LIST } from "@/lib/constants";
import {
  LayoutDashboard, Building2, Layers, HardHat, FolderOpen, MessageSquare,
  Users, UserCog, Globe, Bell, LogOut, Menu, X, ExternalLink,
} from "lucide-react";

function NavItem({ to, icon: Icon, label, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm rounded-none border-l-2 transition-colors ${
          isActive
            ? "bg-white border-ink text-ink font-medium"
            : "border-transparent text-ink/70 hover:text-ink hover:bg-white/70"
        }`
      }
    >
      <Icon size={16} strokeWidth={1.6} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);

  if (!user) return null;

  const doLogout = async () => {
    await logout();
    nav("/staff/login");
  };

  const canDept = (d) => canAccessDept(user, d);

  return (
    <div className="min-h-screen flex portal-bg text-ink" data-testid="portal-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-30 h-screen w-72 bg-sand border-r border-line flex flex-col transition-transform ${
          openMobile ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-6 py-6 border-b border-line flex items-center justify-between">
          <Link to="/portal" className="font-heading text-base tracking-tight">
            <span className="font-light">AS</span> <span className="font-medium">STUDIOS</span>
            <div className="eyebrow text-stone mt-0.5">Staff Portal</div>
          </Link>
          <button className="lg:hidden" onClick={() => setOpenMobile(false)} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto portal-scroll py-4 px-3 space-y-1">
          <NavItem to="/portal" icon={LayoutDashboard} label="Dashboard" end />

          <div className="eyebrow px-3 pt-6 pb-2 text-stone">Departments</div>
          {canDept("architecture") && <NavItem to="/portal/departments/architecture" icon={Building2} label="Architecture" />}
          {canDept("3d_visualisation") && <NavItem to="/portal/departments/3d_visualisation" icon={Layers} label="3D Visualisation" />}
          {canDept("construction") && <NavItem to="/portal/departments/construction" icon={HardHat} label="Construction" />}

          <div className="eyebrow px-3 pt-6 pb-2 text-stone">Studio</div>
          <NavItem to="/portal/projects" icon={FolderOpen} label="Projects" />
          <NavItem to="/portal/enquiries" icon={MessageSquare} label="Enquiries" />
          <NavItem to="/portal/team" icon={Users} label="Team" />
          <NavItem to="/portal/notifications" icon={Bell} label="Notifications" />

          {isSuperAdmin(user) && (
            <>
              <div className="eyebrow px-3 pt-6 pb-2 text-stone">Admin</div>
              <NavItem to="/portal/staff" icon={UserCog} label="Staff Management" />
              <NavItem to="/portal/site-content" icon={Globe} label="Website" />
            </>
          )}
        </nav>

        <div className="border-t border-line p-4">
          <div className="flex items-start gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center font-mono text-sm">
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="portal-user-name">{user.name}</div>
              <div className="text-xs text-stone font-mono">{ROLE_LABEL[user.role] || user.role}</div>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex-1 border border-line px-3 py-2 text-xs uppercase tracking-widest text-stone hover:text-ink hover:border-ink text-center inline-flex items-center justify-center gap-1.5"
            >
              <ExternalLink size={12} /> Site
            </a>
            <button
              onClick={doLogout}
              data-testid="portal-logout"
              className="flex-1 border border-line px-3 py-2 text-xs uppercase tracking-widest text-stone hover:text-destructive hover:border-destructive inline-flex items-center justify-center gap-1.5"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-20 h-14 bg-white border-b border-line flex items-center justify-between px-4">
        <button data-testid="portal-mobile-menu" onClick={() => setOpenMobile((v) => !v)}>
          <Menu size={20} />
        </button>
        <Link to="/portal" className="font-heading text-sm">AS · Staff Portal</Link>
        <button onClick={doLogout} className="text-xs uppercase tracking-widest text-stone"><LogOut size={16} /></button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>

      {openMobile && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-black/30"
          onClick={() => setOpenMobile(false)}
        />
      )}
    </div>
  );
}
